/**
 * VacationAccountCard.js
 * Displays vacation account summary for a staff member
 * Shows entitlement, carryover, adjustments, used, and remaining days
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useVacation } from '../../contexts/VacationContext';
import { useTranslation } from 'react-i18next';
import '../../styles/vacation.css';

const VacationAccountCard = ({ staffId, companyId, onManageClick, refreshTrigger }) => {
  const { t } = useTranslation('staff');
  const {
    vacationAccount,
    selectedYear,
    setSelectedYear,
    accountLoading,
    accountError,
    loadVacationAccount,
    checkAccountExists
  } = useVacation();

  const [accountExists, setAccountExists] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [isInitializing, setIsInitializing] = useState(true);

  /**
   * Generate year options (current year - 1 to current year + 2)
   */
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 1; i <= currentYear + 2; i++) {
      years.push(i.toString());
    }
    setAvailableYears(years);
  }, []);

  /**
   * Load account when staff, company, year changes, or refresh is triggered
   */
  useEffect(() => {
    const loadAccount = async () => {
      if (!staffId || !companyId || !selectedYear) {
        console.log('Missing required data:', { staffId, companyId, selectedYear });
        setIsInitializing(false);
        return;
      }

      setIsInitializing(true);
      try {
        console.log('Loading vacation account for:', { staffId, companyId, selectedYear });
        const exists = await checkAccountExists(staffId, selectedYear);
        console.log('Account exists:', exists);
        setAccountExists(exists);

        if (exists) {
          await loadVacationAccount(staffId, selectedYear);
        }
      } catch (error) {
        console.error('Error loading vacation account:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    loadAccount();
  }, [staffId, companyId, selectedYear, loadVacationAccount, checkAccountExists, refreshTrigger]);

  /**
   * Handle year change
   */
  const handleYearChange = useCallback((e) => {
    const newYear = e.target.value;
    console.log('Year changed to:', newYear);
    setSelectedYear(newYear);
  }, [setSelectedYear]);

  /**
   * Handle initialize/manage button click
   */
  const handleManageClick = useCallback(() => {
    console.log('Manage/Initialize clicked for year:', selectedYear);
    if (onManageClick) {
      onManageClick(selectedYear);
    } else {
      console.warn('onManageClick handler not provided');
    }
  }, [selectedYear, onManageClick]);

  /**
   * Calculate progress percentage for visual indicator
   */
  const getProgressPercentage = useCallback(() => {
    if (!vacationAccount || vacationAccount.totalEntitlement === 0) return 0;
    return Math.max(0, Math.min(100, (vacationAccount.remainingDays / vacationAccount.totalEntitlement) * 100));
  }, [vacationAccount]);

  /**
   * Get progress color based on remaining percentage
   */
  const getProgressColor = useCallback(() => {
    const percentage = getProgressPercentage();
    if (percentage > 50) return '#4caf50'; // Green
    if (percentage > 25) return '#ff9800'; // Orange
    return '#f44336'; // Red
  }, [getProgressPercentage]);

  /**
   * Format carryover with expiry warning
   */
  const getCarryoverDisplay = useCallback(() => {
    if (!vacationAccount || vacationAccount.carryOver === 0) {
      return { value: 0, warning: false };
    }

    const isExpired = vacationAccount.isCarryOverExpired();
    const deadline = vacationAccount.carryOverDeadline;

    return {
      value: vacationAccount.carryOver,
      warning: !isExpired && deadline,
      expired: isExpired,
      deadline: deadline ? new Date(deadline).toLocaleDateString('de-DE') : null
    };
  }, [vacationAccount]);

  // Loading state
  if (isInitializing || accountLoading) {
    return (
      <div className="vacation-account-card">
        <div className="card-header">
          <h3>{t('vacationAccount.vacationAccount')}</h3>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('page.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (accountError) {
    return (
      <div className="vacation-account-card">
        <div className="card-header">
          <h3>{t('vacationAccount.vacationAccount')}</h3>
          <select 
            value={selectedYear} 
            onChange={handleYearChange}
            className="year-selector"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="error-container">
          <p className="error-message">{accountError}</p>
          <button 
            className="btn-secondary"
            onClick={() => loadVacationAccount(staffId, selectedYear)}
          >
            {t('timeManagement.refresh')}
          </button>
        </div>
      </div>
    );
  }

  // ✅ FIX: No account exists for selected year OR vacationAccount is null
  if (!accountExists || !vacationAccount) {
    return (
      <div className="vacation-account-card">
        <div className="card-header">
          <h3>{t('vacationAccount.vacationAccount')}</h3>
          <select 
            value={selectedYear} 
            onChange={handleYearChange}
            className="year-selector"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="no-account-container">
          <div className="no-account-icon">📅</div>
          <p className="no-account-message">
            {t('vacationAccount.noVacationAccountForYear', { year: selectedYear })}
          </p>
          <button 
            className="btn-primary"
            onClick={handleManageClick}
          >
            {t('vacationAccount.initializeYear')}
          </button>
        </div>
      </div>
    );
  }

  // ✅ FIX: Additional safety check - ensure vacationAccount has required properties
  if (!vacationAccount.totalEntitlement && vacationAccount.totalEntitlement !== 0) {
    console.error('Invalid vacation account data:', vacationAccount);
    return (
      <div className="vacation-account-card">
        <div className="card-header">
          <h3>{t('vacationAccount.vacationAccount')}</h3>
          <select 
            value={selectedYear} 
            onChange={handleYearChange}
            className="year-selector"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="error-container">
          <p className="error-message">{t('vacationAccount.invalidAccountData')}</p>
          <button 
            className="btn-secondary"
            onClick={() => loadVacationAccount(staffId, selectedYear)}
          >
            {t('timeManagement.refresh')}
          </button>
        </div>
      </div>
    );
  }

  const carryoverInfo = getCarryoverDisplay();
  const progressPercentage = getProgressPercentage();
  const progressColor = getProgressColor();

  return (
    <div className="vacation-account-card">
      {/* Header with Year Selector */}
      <div className="card-header">
        <div className="header-left">
          <h3>{t('vacationAccount.vacationAccount')}</h3>
        </div>
        <div className="header-right">
          <select 
            value={selectedYear} 
            onChange={handleYearChange}
            className="year-selector"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button 
            className="btn-secondary manage-btn"
            onClick={handleManageClick}
          >
            {t('vacationAccount.manageAccount')}
          </button>
        </div>
      </div>

      {/* Visual Progress Indicator */}
      <div className="progress-section">
        <div className="progress-ring-container">
          <svg className="progress-ring" width="140" height="140">
            <circle
              className="progress-ring-bg"
              cx="70"
              cy="70"
              r="60"
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="12"
            />
            <circle
              className="progress-ring-fill"
              cx="70"
              cy="70"
              r="60"
              fill="none"
              stroke={progressColor}
              strokeWidth="12"
              strokeDasharray={`${(progressPercentage / 100) * 377} 377`}
              strokeLinecap="round"
              transform="rotate(-90 70 70)"
            />
          </svg>
          <div className="progress-center">
            <span className="remaining-days">{vacationAccount.remainingDays || 0}</span>
            <span className="remaining-label">{t('vacationAccount.daysRemaining')}</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-label">{t('vacationAccount.totalEntitlement')}</span>
            <span className="stat-value">{vacationAccount.totalEntitlement || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('vacationAccount.usedDays')}</span>
            <span className="stat-value used">{vacationAccount.usedDays || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('vacationAccount.remainingDays')}</span>
            <span className="stat-value remaining">{vacationAccount.remainingDays || 0}</span>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="breakdown-section">
        <h4 className="breakdown-title">{t('vacationAccount.entitlementBreakdown')}</h4>
        
        <div className="breakdown-grid">
          {/* Base Entitlement */}
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>{t('vacationAccount.baseEntitlement')}</span>
            </div>
            <div className="breakdown-value">
              {vacationAccount.baseEntitlement || 0} {t('vacationAccount.days')}
            </div>
          </div>

          {/* Carryover */}
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>{t('vacationAccount.carryover')}</span>
              {carryoverInfo.warning && (
                <span className="carryover-warning" title={t('vacationAccount.expiresOn', { date: carryoverInfo.deadline })}>
                  ⚠️
                </span>
              )}
              {carryoverInfo.expired && (
                <span className="carryover-expired" title={t('vacationAccount.expiredOn', { date: carryoverInfo.deadline })}>
                  ❌
                </span>
              )}
            </div>
            <div className="breakdown-value">
              +{carryoverInfo.value} {t('vacationAccount.days')}
            </div>
          </div>

          {/* Adjustments */}
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>{t('vacationAccount.adjustments')}</span>
              {vacationAccount.adjustments?.length > 0 && (
                <span className="adjustment-count">({vacationAccount.adjustments.length})</span>
              )}
            </div>
            <div className="breakdown-value">
              {vacationAccount.getTotalAdjustments() >= 0 ? '+' : ''}
              {vacationAccount.getTotalAdjustments()} {t('vacationAccount.days')}
            </div>
          </div>

          {/* Total Entitlement */}
          <div className="breakdown-item total">
            <div className="breakdown-label">
              <span>{t('vacationAccount.totalAvailable')}</span>
            </div>
            <div className="breakdown-value total-value">
              = {vacationAccount.totalEntitlement || 0} {t('vacationAccount.days')}
            </div>
          </div>

          {/* Used Days */}
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>{t('vacationAccount.usedDays')}</span>
            </div>
            <div className="breakdown-value used-value">
              -{vacationAccount.usedDays || 0} {t('vacationAccount.days')}
            </div>
          </div>

          {/* Remaining Days */}
          <div className="breakdown-item remaining-item">
            <div className="breakdown-label">
              <span>{t('vacationAccount.remaining')}</span>
            </div>
            <div className="breakdown-value remaining-value">
              = {vacationAccount.remainingDays || 0} {t('vacationAccount.days')}
            </div>
          </div>
        </div>
      </div>

      {/* Carryover Warning Message */}
      {carryoverInfo.warning && (
        <div className="carryover-alert warning">
          <span className="alert-icon">⚠️</span>
          <span>
            {t('vacationAccount.carryoverExpiresMessage', { 
              days: carryoverInfo.value, 
              date: carryoverInfo.deadline 
            })}
          </span>
        </div>
      )}

      {/* Carryover Expired Message */}
      {carryoverInfo.expired && (
        <div className="carryover-alert expired">
          <span className="alert-icon">❌</span>
          <span>
            {t('vacationAccount.carryoverExpiredMessage', { 
              days: carryoverInfo.value, 
              date: carryoverInfo.deadline 
            })}
          </span>
        </div>
      )}

      {/* Low Balance Warning */}
      {vacationAccount.remainingDays < 5 && vacationAccount.remainingDays > 0 && (
        <div className="carryover-alert low-balance">
          <span className="alert-icon">⚠️</span>
          <span>{t('vacationAccount.lowVacationBalanceWarning')}</span>
        </div>
      )}

      {/* No Days Remaining */}
      {vacationAccount.remainingDays <= 0 && (
        <div className="carryover-alert no-balance">
          <span className="alert-icon">🚫</span>
          <span>{t('vacationAccount.noVacationDaysRemaining')}</span>
        </div>
      )}

      {/* Last Updated */}
      <div className="card-footer">
        <span className="last-updated">
          Last updated: {new Date(vacationAccount.updatedAt || Date.now()).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      </div>
    </div>
  );
};

export default VacationAccountCard;